import type React from 'react';
import { useEffect, useMemo, useState } from 'react';

import type { Column } from '@/components/Table/Table';

type TableSettings = {
    columnOrder: string[];
    hiddenColumns: string[];
};

export function useTableSettings<T>(tableId: string, columns: Column<T>[], defaultHidden: string[]) {
    const columnKeys = useMemo(() => columns.map(c => c.key), [columns]);

    const localStorageKey = `tableSettings.${tableId}`;

    const savedSettings: TableSettings | null = useMemo(() => {
        const stored = localStorage.getItem(localStorageKey);
        return stored ? JSON.parse(stored) : null;
    }, []);

    // Append columns added after the user's settings were saved so they still show up.
    const newColumns = useMemo(
        () => columnKeys.filter(key => !savedSettings?.columnOrder.includes(key)),
        [savedSettings, columnKeys]
    );

    const [columnOrder, setColumnOrder] = useState<string[]>([
        ...(savedSettings?.columnOrder ?? columnKeys),
        ...(savedSettings ? newColumns : []),
    ]);
    const [hiddenColumns, setHiddenColumns] = useState<string[]>(
        savedSettings
            ? [...savedSettings.hiddenColumns, ...newColumns.filter(key => defaultHidden.includes(key))]
            : defaultHidden
    );

    useEffect(() => {
        localStorage.setItem(localStorageKey, JSON.stringify({ columnOrder, hiddenColumns }));
    }, [columnOrder, hiddenColumns]);

    const visibleColumns = useMemo(
        () =>
            columnOrder
                .map(key => columns.find(c => c.key === key))
                .filter((c): c is Column<T> => !!c)
                .filter(c => !hiddenColumns.includes(c.key)),
        [columnOrder, hiddenColumns, columns]
    );

    const handleColumnChange = (newOrder: string[], newHidden: string[]) => {
        setColumnOrder(newOrder);
        setHiddenColumns(newHidden);
    };

    const columnLabels = useMemo(
        () =>
            columns.reduce<Record<string, React.ReactNode>>((acc, col) => {
                acc[col.key] = col.label;
                return acc;
            }, {}),
        [columns]
    );

    return {
        columnOrder,
        hiddenColumns,
        visibleColumns,
        handleColumnChange,
        columnLabels,
    };
}
